import React, { useState, useEffect } from 'react';
import {
  Card,
  List,
  Tag,
  Typography,
  Space,
  Button,
  Modal,
  message,
  Tooltip,
  Divider,
  Alert,
} from 'antd';
import {
  HistoryOutlined,
  EyeOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { templateService, TemplateVersion } from '../services/templateService';
import { useI18n } from '../hooks/useI18n';

const { Title, Text } = Typography;

interface TemplateVersionHistoryProps {
  templateId: number;
}

const TemplateVersionHistory: React.FC<TemplateVersionHistoryProps> = ({ templateId }) => {
  const [versions, setVersions] = useState<TemplateVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<TemplateVersion | null>(null);
  const [versionContent, setVersionContent] = useState<string>('');
  const { t } = useI18n();

  useEffect(() => {
    loadVersions();
  }, [templateId]);

  const loadVersions = async () => {
    try {
      setLoading(true);
      const data = await templateService.getVersions(templateId);
      setVersions(data);
    } catch (error: any) {
      message.error(t('templates.version.loadFailed'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewVersion = async (version: TemplateVersion) => {
    try {
      setSelectedVersion(version);
      setViewModalVisible(true);
      
      // 这里应该从版本记录中获取内容，暂时使用当前Template的JSON
      const jsonTemplate = await templateService.convertToJsonTemplate(templateId);
      setVersionContent(JSON.stringify(jsonTemplate, null, 2));
    } catch (error: any) {
      message.error(t('templates.version.getContentFailed'));
      console.error(error);
    }
  };

  const handleDownloadVersion = async (version: TemplateVersion) => {
    try {
      const jsonTemplate = await templateService.convertToJsonTemplate(templateId);
      const jsonString = JSON.stringify(jsonTemplate, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `template_v${version.version}.json`;
      a.click();
      URL.revokeObjectURL(url);
      message.success(t('templates.version.exportSuccess'));
    } catch (error: any) {
      message.error(t('templates.version.exportFailed'));
      console.error(error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getVersionColor = (version: string) => {
    if (version.startsWith('1.0')) return 'green';
    if (version.startsWith('2.0')) return 'blue';
    if (version.startsWith('3.0')) return 'purple';
    return 'default';
  };

  return (
    <div>
      <Alert
        message={t('templates.version.title')}
        description={t('templates.version.desc')}
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Card>
        <div style={{ marginBottom: 16 }}>
          <Space>
            <HistoryOutlined />
            <Title level={5} style={{ margin: 0 }}>
              {t('templates.version.listTitle')}
            </Title>
            <Text type="secondary">({versions.length} {t('templates.version.countUnit')})</Text>
          </Space>
        </div>

        <List
          loading={loading}
          dataSource={versions}
          renderItem={(version, index) => (
            <List.Item
              key={version.id}
              actions={[
                <Tooltip title={t('templates.version.viewTip')}>
                  <Button
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() => handleViewVersion(version)}
                  >
                    {t('templates.version.view')}
                  </Button>
                </Tooltip>,
                <Tooltip title={t('templates.version.exportTip')}>
                  <Button
                    type="link"
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownloadVersion(version)}
                  >
                    {t('templates.version.export')}
                  </Button>
                </Tooltip>,
              ]}
            >
              <List.Item.Meta
                avatar={
                  <div style={{ 
                    width: 40, 
                    height: 40, 
                    borderRadius: '50%', 
                    background: '#f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}>
                    v{version.version.split('.')[0]}
                  </div>
                }
                title={
                  <Space>
                    <Tag color={getVersionColor(version.version)}>
                      {t('templates.version.version')} {version.version}
                    </Tag>
                    {index === 0 && <Tag color="gold">{t('templates.version.latest')}</Tag>}
                  </Space>
                }
                description={
                  <div>
                    <div style={{ marginBottom: 4 }}>
                      <Text strong>{t('templates.version.createdAt')}：</Text>
                      <Text>{formatDate(version.created_at)}</Text>
                    </div>
                    {version.created_by && (
                      <div style={{ marginBottom: 4 }}>
                        <Text strong>{t('templates.version.createdBy')}：</Text>
                        <Text>{version.created_by}</Text>
                      </div>
                    )}
                    {version.change_description && (
                      <div>
                        <Text strong>{t('templates.version.changeDesc')}：</Text>
                        <Text>{version.change_description}</Text>
                      </div>
                    )}
                  </div>
                }
              />
            </List.Item>
          )}
        />
      </Card>

      {/* 版本内容查看模态框 */}
      <Modal
        title={
          <Space>
            <InfoCircleOutlined />
            {t('templates.version.modalTitle')} - {selectedVersion?.version}
          </Space>
        }
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        width="80%"
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            {t('common.close')}
          </Button>,
          <Button
            key="download"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => selectedVersion && handleDownloadVersion(selectedVersion)}
          >
            {t('templates.version.export')}
          </Button>,
        ]}
      >
        <div>
          <Alert
            message={t('templates.version.info')}
            description={
              <div>
                <div><Text strong>{t('templates.version.version')}：</Text>{selectedVersion?.version}</div>
                <div><Text strong>{t('templates.version.createdAt')}：</Text>{selectedVersion && formatDate(selectedVersion.created_at)}</div>
                {selectedVersion?.created_by && (
                  <div><Text strong>{t('templates.version.createdBy')}：</Text>{selectedVersion.created_by}</div>
                )}
                {selectedVersion?.change_description && (
                  <div><Text strong>{t('templates.version.changeDesc')}：</Text>{selectedVersion.change_description}</div>
                )}
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          
          <Divider />
          
          <div>
            <Title level={5}>{t('templates.version.jsonTitle')}</Title>
            <pre style={{ 
              background: '#f5f5f5', 
              padding: 16, 
              borderRadius: 4,
              maxHeight: '60vh',
              overflow: 'auto',
              fontSize: '12px',
              lineHeight: '1.4'
            }}>
              {versionContent}
            </pre>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TemplateVersionHistory;
